import { useAsync } from "@react-hookz/web";
import { useCallback } from "react";
import { useSupabaseCallback } from "../../server/supabase/hooks";

import type { Database } from "../../__definitions__/database-definitions";
import type { SupabaseHandlerArg, SupabaseSchemaName } from "../../server/supabase/hooks/types";
import type { DependencyList } from "react";

export type UseAsyncStateCallback<T, U extends any[] = []> =
  | {
      data: T;
      loading: false;
      error: null;
      load: (...args: U) => Promise<T>;
    }
  | {
      data: null;
      loading: true;
      error: null;
      load: (...args: U) => Promise<T>;
    }
  | {
      data: null;
      loading: false;
      error: Error;
      load: (...args: U) => Promise<T>;
    }
  | {
      data: null;
      loading: false;
      error: null;
      load: (...args: U) => Promise<T>;
    };

export function useAsyncStateCallback<T, U extends unknown[]>(
  callback: (...args: U) => Promise<T>,
  deps: DependencyList,
): UseAsyncStateCallback<T, U> {
  const cb = useCallback(callback, deps); // Do not add saverFn as dep

  const [state, actions] = useAsync(cb);

  if (state.status === "not-executed")
    return { data: null, loading: false, error: null, load: actions.execute };

  if (state.status === "success" && state.result !== undefined)
    return { data: state.result, loading: false, error: null, load: actions.execute };

  if (state.status === "error" && state.error !== undefined)
    return { data: null, loading: false, error: state.error, load: actions.execute };

  return { data: null, loading: true, error: null, load: actions.execute };
}

export function useAsyncStateSupabaseCallback<
  T,
  U extends unknown[],
  SchemaName extends SupabaseSchemaName<Database> = "public",
>(
  callback: (arg0: SupabaseHandlerArg<SchemaName>, ...args: U) => Promise<T>,
  deps: DependencyList,
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  schema: SchemaName = "public" as SchemaName,
): UseAsyncStateCallback<T | undefined, U> {
  const inner = useSupabaseCallback(callback, [schema, ...deps], schema);

  return useAsyncStateCallback(inner, [inner, ...deps, schema]);
}
